import { Lightning, Log, Router } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { ReminderModel } from '../../Business/reminder/reminder.model-shared';
import { fullUrlWithRecipe, Recipes } from '../../Business/utility/ImageRecipeUtil';
import { Button } from '../shared/Button';
import { Backend } from '../../api/Backend';
import { DetailTranslationKeys, PopupTranslationKeys } from '../../api/models/TranslationKeys';
import { LinearStreamInfo } from '../../Business/player/streamInfo/LinearStreamInfo';
import { hasReplayRights } from '../../Business/detail/epg/EpgWatchability';
import { EpgEvent } from '../../Business/player/streamInfo/StreamInfo';
import { PlayerPage } from '../../pages/PlayerPage';
import { ChannelManager } from '../../api/ChannelManager';
import { EpgScheduleHelper } from '../../Business/EpgScheduleHelper';
import { DetailPage } from '../../pages/detail/DetailPage';
import { MainPage } from '../../pages/main/MainPage';

export class ReminderDialog extends Lightning.Component {
  static override _template() {
    return {
      x: 0,
      y: 0,
      w: (w: number) => w,
      h: (h: number) => h,
      color: Theme.Colors.MessageDialog.Background,
      rect: true,
      Content: {
        mount: 0.5,
        x: (w: number) => w / 2,
        y: (h: number) => h / 2,
        w: 1200,
        h: 288,
        Image: {},
        Info: {
          w: 600,
          h: (h: number) => h,
          x: 562,
          Title: {
            w: (w: number) => w,
            text: {
              ...Theme.Fonts.Reminder.Title,
              maxLines: 2,
              wordWrapWidth: (w: number) => w,
            },
          },
          Description: {
            y: 100,
            w: (w: number) => w,
            text: {
              ...Theme.Fonts.Reminder.Description,
              wordWrapWidth: (w: number) => w,
              maxLines: 3,
            },
          },
          Buttons: {
            y: (h: number) => h,
            mountY: 1,
            flex: { direction: 'row' },
            Watch: {
              flexItem: { marginRight: 10 },
              type: Button,
              label: '',
              signals: {
                onActivate: 'play',
              },
            },
            Detail: {
              flexItem: { marginRight: 10 },
              type: Button,
              label: '',
              signals: {
                onActivate: 'detail',
              },
            },
            Cancel: {
              type: Button,
              label: '',
              signals: {
                onActivate: 'dismiss',
              },
            },
          },
        },
      },
    };
  }

  private focusIdx = 0;
  private _backend?: Backend;
  private _data?: ReminderModel;

  override _getFocused() {
    return this.tag('Buttons').children[this.focusIdx];
  }

  set data(data: ReminderModel) {
    this._data = data;
    this.tag('Image').src = this._backend
      ? fullUrlWithRecipe(this._backend, data.schedule.program.image.poster, Recipes.Reminder)
      : '';
    this.tag('Title').text.text = data.schedule.program.text.title ?? '';
    this.tag('Description').text.text = data.schedule.program.text.shortDescription ?? '';
  }

  set backend(backend: Backend) {
    this._backend = backend;
    this.tag('Watch').label = backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now);
    this.tag('Detail').label = backend.translations.forKey(PopupTranslationKeys.popup_reminder_option_details);
    this.tag('Cancel').label = backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel);
  }

  override _handleLeft() {
    this.focusIdx = Math.max(0, this.focusIdx - 1);
  }

  override _handleRight() {
    this.focusIdx = Math.min(this.tag('Buttons').children.length - 1, this.focusIdx + 1);
  }

  override _handleBack() {
    this.dismiss();
  }

  dismiss() {
    this.fireAncestors('$closeDialog');
  }

  play() {
    if (!this._backend || !this._data) return Log.error('[ReminderDialog]', 'No data or backend in dialog');
    ChannelManager.channel(this._backend, this._data.schedule.schedule.channelId).then(channel => {
      if (!channel) return Log.error('[ReminderDialog]', 'Could not find channel for reminder');
      // get the data for the *current* running schedule on the channel
      EpgScheduleHelper.getCurrentScheduleOfChannel(this._backend!, channel.channelId).then(scheduleData => {
        if (!scheduleData) return Log.error('[ReminderDialog]', 'Could not find current scheduledata for reminder');
        const epgEvent: EpgEvent = {
          channel: channel,
          program: scheduleData.program,
          season: scheduleData.season,
          series: scheduleData.series,
          schedule: scheduleData.schedule,
        };
        const streamInfo = new LinearStreamInfo(epgEvent, this._backend!, hasReplayRights(epgEvent.schedule, channel));

        if (streamInfo) {
          if (Router.getActiveHash() === 'play') {
            (Router.getActivePage() as PlayerPage).closePlayerModals();
            this._backend!.playerController.changeStream(streamInfo);
            this.dismiss();
          } else {
            Router.navigate('play', { streamInfo });
            this.dismiss();
          }
        }
      });
    });
  }

  detail() {
    if (!this._backend || !this._data) return Log.error('[ReminderDialog]', 'No data or backend in dialog');
    this.dismiss();
    if (Router.getActiveHash() === 'play') {
      (Router.getActivePage() as PlayerPage).$openPlayerModal(DetailPage, {
        backend: this._backend,
        assetType: this._data.type,
        assetId: this._data.id,
      });
    } else if (Router.getActiveHash() === 'main') {
      (Router.getActivePage() as MainPage).$openModal(DetailPage, {
        backend: this._backend,
        assetType: this._data.type,
        assetId: this._data.id,
      });
    }
  }
}
